import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/store/authStore";
import {
  fetchSecuritySettings,
  updateSecurityTwoFactor,
  updatePassword,
  deleteUserAccount,
  type PasswordChangeForm,
} from "@/services/settings";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { GeofencingSecurityCard } from "./GeofencingSecurityCard";
import { useStoreManagementStore } from "@/store/storeManagementStore";
// Helper function to clean up local storage for auth
const cleanupAuthState = () => {
  // Remove all Supabase auth keys from localStorage
  //   Object.keys(localStorage).forEach((key) => {
  //     if (
  //       key.startsWith('supabase.auth.') ||
  //       key.includes('sb-') ||
  //       key.includes('Xerven-')
  //     ) {
  //       localStorage.removeItem(key);
  //     }
  //   }
  // );
  // Do the same for sessionStorage if in use
  // Object.keys(sessionStorage || {}).forEach((key) => {
  //   if (
  //     key.startsWith("supabase.auth.") ||
  //     key.includes("sb-") ||
  //     key.includes("Xerven-")
  //   ) {
  //     sessionStorage.removeItem(key);
  //   }
  // });
};

export function SecuritySettings() {
  const { t } = useTranslation();

  const { user, logout } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState<PasswordChangeForm>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const { stores, fetchStores, selectedStoreId } = useStoreManagementStore();
  // Check if user is a manager or higher
  useEffect(() => {
    if (user?.role) {
      setIsManager(["manager", "admin", "super_admin"].includes(user.role));
    }
  }, [user?.role]);

  // Load security settings from database
  useEffect(() => {
    const loadSecuritySettings = async () => {
      if (!user?.id) return;

      try {
        const settings = await fetchSecuritySettings(user.id);

        if (settings) {
          setTwoFactorEnabled(settings.two_factor_enabled);
        }
      } catch (error) {
        console.error("Error loading security settings:", error);
      }
    };

    loadSecuritySettings();
  }, [user?.id]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !isManager) return;

    setIsLoading(true);

    try {
      const success = await updatePassword(user.id, passwordForm);

      if (success) {
        // Reset the form
        setPasswordForm({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnableOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !isManager) return;

    setIsLoading(true);

    try {
      await updateSecurityTwoFactor(user.id, !twoFactorEnabled);
      setTwoFactorEnabled(!twoFactorEnabled);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.id) return;

    setIsDeleteLoading(true);

    try {
      const success = await deleteUserAccount(user.id);

      if (success) {
        toast.success("Account deleted successfully");
        setShowDeleteDialog(false);

        // Clean up all auth state in local/session storage
        // cleanupAuthState();

        // Log out the user after account deletion
        setTimeout(() => {
          logout().then(() => {
            // Force a full page reload to clear any remaining state
            window.location.href = "/";
          });
        }, 1500);
      }
    } finally {
      setIsDeleteLoading(false);
    }
  };

  // If user isn't a manager, show a message
  if (!isManager) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>{t("securitySettings")}</CardTitle>
          <CardDescription>{t("managerPermissionsRequired")}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("changePassword")}</CardTitle>
          <CardDescription>{t("updatePasswordRegularly")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="currentPassword">{t("currentPassword")}</Label>
              <Input
                id="currentPassword"
                name="currentPassword"
                type="password"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="newPassword">{t("newPassword")}</Label>
              <Input
                id="newPassword"
                name="newPassword"
                type="password"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="confirmPassword">{t("confirmNewPassword")}</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                disabled={isLoading}
              />
            </div>

            <Button type="submit" disabled={isLoading}>
              {isLoading ? t("saving") : t("changePassword")}
            </Button>
          </form>
        </CardContent>
      </Card>

      {selectedStoreId && (
        <GeofencingSecurityCard
          store={stores.find((store) => store.id === selectedStoreId)!}
          onUpdate={fetchStores}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t("twoFactorAuthentication")}</CardTitle>
          <CardDescription>{t("extraSecurityLayer")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleEnableOtp} className="space-y-4">
            <div className="grid gap-2">
              <Label>{t("twoFactorStatus")}</Label>
              <div className="bg-muted p-2 rounded-md text-sm">
                {t("twoFactorStatus")}{" "}
                <span
                  className={
                    twoFactorEnabled
                      ? "text-green-500 font-medium"
                      : "text-destructive font-medium"
                  }
                >
                  {twoFactorEnabled ? t("enabled") : t("disabled")}
                </span>
                .
              </div>
            </div>

            <Button type="submit" disabled={isLoading}>
              {isLoading
                ? t("processing")
                : twoFactorEnabled
                ? t("disableTwoFactor")
                : t("enableTwoFactor")}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-destructive">Delete Account</CardTitle>
          <CardDescription>
            Permanently delete your account and all associated data.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/50 border border-destructive/20 p-4 rounded-md mb-4">
            <p className="text-sm text-muted-foreground">
              Warning: This action is irreversible. All your data, including
              stores, orders, menu items, and settings will be permanently
              deleted.
            </p>
          </div>
          <Button
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
          >
            Delete Account
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Account Permanently
            </AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-4">
                <p>
                  This action is permanent and cannot be undone. When you delete
                  your account:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  <li>All your personal information will be deleted</li>
                  <li>All stores you created will be removed</li>
                  <li>All menu items, orders, and tables will be removed</li>
                  <li>
                    All employees associated with your stores will lose access
                  </li>
                  <li>All security and notification settings will be lost</li>
                  <li>You will be immediately logged out</li>
                </ul>
                <p className="font-medium mt-4">
                  Are you absolutely sure you want to proceed?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteAccount}
              disabled={isDeleteLoading}
            >
              {isDeleteLoading ? "Deleting..." : "Yes, Delete My Account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
